if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // ── Autoplay on load ───────────────────────────────────────────────
        // Dawn never calls playActiveMedia() until a slide change happens, so
        // a video that's the initial/featured slide just sits on its poster
        // until clicked. Load + play it immediately, and pre-load (muted,
        // paused) every other video slide so switching to them later is
        // instant rather than starting from a cold poster.
        this.autoplayOnLoad();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);

        // Previously only thumbnail clicks / variant swaps (via setActiveMedia)
        // triggered video playback — swiping to a video slide just left it
        // sitting on its poster. This is what makes swipe navigation actually
        // play the slide you land on, same as clicking its thumbnail would.
        // Safe to call even when the slide is already playing / mid-load —
        // see the `video.paused` guard added in playActiveMedia() below,
        // which prevents this from double-firing play() on a video that a
        // thumbnail click already started (that click's own scroll will also
        // land here ~500ms later via this same debounced listener).
        this.playActiveMedia(event.detail.currentElement);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        // NOTE: deliberately NOT calling window.pauseAllMedia() here.
        // That pauses every <video> on the page, which would stop other
        // gallery slides' videos from continuing to play in the background
        // while the user looks at a different slide/image. We only want to
        // pause whichever single video was active immediately before this
        // one, not the whole page.
        this.elements.viewer.querySelectorAll('.deferred-media').forEach((dm) => {
          if (dm === activeItem.querySelector('.deferred-media')) return;
          const video = dm.querySelector('video:not(.fae-thumb-video)');
          if (video && !video.paused) video.pause();
        });

        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) {
          if (!deferredMedia.getAttribute('loaded')) {
            // First load — inject video from <template>.
            // Per-instance patch in pdp-sticky-atc.js handles unmuting.
            deferredMedia.loadContent(false);
          } else {
            // Already loaded — just (re)play it with sound.
            const video = deferredMedia.querySelector('video:not(.fae-thumb-video)');
            if (video) {
              video.removeAttribute('muted'); // must remove HTML attribute, not just JS property
              video.muted  = false;
              video.volume = 1;
              // Only call play() if it's actually paused. Calling play() on an
              // already-playing video throws AbortError, whose catch block
              // below would otherwise re-mute it — this matters now that
              // playActiveMedia() can be called twice for the same slide
              // (once from a thumbnail/swatch click, once again from that
              // click's own scroll echoing through onSlideChanged).
              if (video.paused) {
                video.play().catch(() => {
                  video.muted = true;
                  video.play();
                });
              }
            }
          }
        }

        // Thumbnail-strip videos are always muted and should loop continuously.
        // Nudge any that got suspended (low-power mode, tab backgrounded, etc.)
        // back to playing. This runs on every slide change intentionally —
        // it only restarts videos that are actually paused, images are unaffected.
        document.querySelectorAll('.fae-thumb-video').forEach((v) => {
          if (v.paused) {
            v.muted = true;
            v.play().catch(() => {});
          }
        });
      }

      // ── Autoplay every video slide on load ──────────────────────────────
      // Runs once, when the gallery first connects. Loads the active slide's
      // video immediately (with sound) so it starts playing without a click,
      // and loads every other video slide in the background (muted, paused)
      // so that switching to them later is instant instead of starting cold
      // from a poster image.
      autoplayOnLoad() {
        const slides = this.elements.viewer.querySelectorAll('[data-media-id]');
        if (!slides.length) return;

        slides.forEach((slide) => {
          const deferredMedia = slide.querySelector('.deferred-media');
          if (!deferredMedia || deferredMedia.getAttribute('loaded')) return;

          const isActive = slide.classList.contains('is-active');

          if (isActive) {
            // Active slide: load + play with sound, same as a real slide change.
            deferredMedia.loadContent(false);
            return;
          }

          // Inactive slide: only pre-load native Shopify-hosted <video> here.
          // External video (YouTube/Vimeo) renders as an <iframe> with no
          // reliable way to mute it from outside after the fact, so loading
          // one in the background would autoplay with sound the user didn't
          // ask for — worse than the original click-to-play behaviour. Those
          // stay untouched until the user actually reaches that slide, at
          // which point playActiveMedia() loads + plays them normally.
          const templateRoot = deferredMedia.querySelector('template').content.firstElementChild;
          if (templateRoot.tagName !== 'VIDEO') return;

          // Load the video into the DOM but keep it muted and paused so it
          // doesn't compete with the active slide's audio. It'll be unmuted
          // and resumed by playActiveMedia() once the user reaches it.
          // (Deliberately not calling deferredMedia.loadContent() here — it
          // calls window.pauseAllMedia() unconditionally, which would stop
          // the active slide's video right after we just started it.)
          window.setTimeout(() => {
            if (deferredMedia.getAttribute('loaded')) return;
            const content = document.createElement('div');
            content.appendChild(deferredMedia.querySelector('template').content.firstElementChild.cloneNode(true));
            deferredMedia.setAttribute('loaded', true);
            const el = deferredMedia.appendChild(content.querySelector('video'));
            if (!el) return;
            el.muted = true;
            el.setAttribute('muted', '');
            el.play().catch(() => {});
            // Workaround for Safari iframe bug, same as Dawn's loadContent()
            const formerStyle = el.getAttribute('style');
            el.setAttribute('style', 'display: block;');
            window.setTimeout(() => {
              el.setAttribute('style', formerStyle);
            }, 0);
          });
        });
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}